import { Content } from "../../../general/modules/commons/content/content";
import { ContentHeader } from "../../../general/modules/commons/content/contentHeader";
import { LayoutContent } from "../../../general/modules/commons/layout/layout";
import useCrudForm from "../../../../hooks/useCrudForm";
import {
  RequestResponseModel,
  requestSchema,
  requestMutationModel,
} from "../../domain/models/requestModel";
import { RequestTiresetFormTemplate } from "../commons/requestTiresetFormTemplate/requestTiresetFormTemplate";
import { useTranslation } from "react-i18next";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../hooks/useTypedSelector";
import {
  getRequest,
  isRequestFinished,
  setLatestRequest,
  updateRequestState,
} from "../../infrastructure/slices/requestMutateSlice";
import "react-step-progress-bar/styles.css";
import { RequestProgressBar } from "../commons/requestProgressBar/requestProgressBar";
import { Badge, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import LicensePlate from "license-plate";
import { Link, useNavigate } from "react-router-dom";
import { datePickerOptions } from "../../../general/modules/commons/crud/inputs/crudInputModel";
import { useAuthorization } from "../../../../hooks/useAuthorization";
import { useEffect, useMemo } from "react";
import {
  clear,
  getTireset,
} from "../../../tireset/infrastructure/slices/tireSetMutateSlice";
import { EntitySelectFieldModel } from "../../../general/fields/modules/commons/entitySelectField/entitySelectFieldModel";
import { relationSchema } from "../../../relation/domain/models/RelationModel";
import { fetchEvents } from "../../../event/infrastructure/eventMutateSlice";
import { toast } from "sonner";
import { EntityLogs } from "../../../logs/modules/features/EntityLogs";
import { CarTireSetResponseModel } from "../../../tireset/domain/models/carTireSetModel";
import { CustomFilters } from "../../../general/crudGraphql/domain/models/graphqlModel";
import { formatDateForServer } from "../../../general/date/infrastructure/helpers/DateConverter";

const RequestMutateForm = ({
  submitButtonLabel,
}: {
  submitButtonLabel?: string;
}) => {
  const dispatch = useAppDispatch();
  const { request } = useAppSelector((state) => state.requestMutate);
  const { tireset } = useAppSelector((state) => state.tiresetMutate);
  useEffect(() => {
    if (request?.source_type === "tireset" && request.source_id) {
      void dispatch(getTireset(request.source_id + ""));
    }

    return () => {
      dispatch(clear());
    };
  }, [request]);

  return (
    <RequestMutateFormInner
      request={request}
      tireset={tireset}
      submitButtonLabel={submitButtonLabel}
    />
  );
};

const RequestMutateFormInner = ({
  request,
  tireset,
  submitButtonLabel,
}: {
  request?: RequestResponseModel;
  tireset?: CarTireSetResponseModel;
  submitButtonLabel?: string;
}) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { hasRole } = useAuthorization();

  const { user } = useAppSelector((state) => state.auth);

  const existingRequests = useMemo(() => {
    return tireset?.requests?.filter(
      (request) =>
        request.state !== "delivered" &&
        request.state !== "cancelled" &&
        request.state !== "in_storage" &&
        request.requestTires?.length !== 0,
    );
  }, [tireset]);

  let selectedTires = [] as number[];
  if (request?.requestTires != undefined) {
    selectedTires = request?.requestTires.map((tire) => {
      return parseInt(tire.tire.id + "");
    });
  }

  const defaultValues = {
    type: request?.type ?? (tireset?.in_hotel ? "deliver" : "pickup"),
    relation_id: request?.relation?.id ?? tireset?.car?.relation?.id,
    source_type: request?.source_type ?? "tireset",
    source_id: request?.source_id
      ? parseInt("" + request?.source_id)
      : parseInt("" + tireset?.id),
    user_id: request?.user?.id ?? user.id,
    licence_plate:
      request?.source?.car?.licence ??
      tireset?.car?.licence ??
      request?.source?.car?.label ??
      tireset?.car?.label,
    tire_type: request?.source?.type ?? tireset?.type,
    invoice_status: request?.invoice_status,
    in_out_storage: request?.in_out_storage
      ? new Date(request?.in_out_storage)
      : null,
    pickup_and_delivery_date: request?.pickup_and_delivery_date
      ? new Date(request?.pickup_and_delivery_date)
      : tireset?.getPickupDeliveryDates?.firstDate
        ? new Date(tireset.getPickupDeliveryDates.firstDate)
        : new Date(),
    work_order_number: request?.work_order_number ?? undefined,
    updated_at: request?.updated_at
      ? new Date(request?.updated_at)
      : new Date(),
    tiresFreestock: selectedTires,
  };

  const formattedLicensePlate = new LicensePlate(
    "" + defaultValues.licence_plate,
  );
  defaultValues.licence_plate = formattedLicensePlate.valid()
    ? formattedLicensePlate.pretty()
    : defaultValues.licence_plate;
  const dispatch = useAppDispatch();

  const selectOptions = useMemo(() => {
    const options: {
      tiresFreestock: { value: number; label: string; isDisabled: boolean }[];
    } = {
      tiresFreestock: [],
    };

    if (request?.source?.tires != undefined) {
      options.tiresFreestock = request?.source?.tires.map((tire) => {
        return {
          value: tire.id,
          label: t(tire?.label ?? ""),
          isDisabled: !tire?.status,
        };
      });
    } else if (tireset?.tires != undefined) {
      options.tiresFreestock = tireset.tires.map((tire) => {
        return {
          value: tire.id,
          label: t(tire?.label ?? ""),
          isDisabled: !tire?.status,
        };
      });
    }

    options.tiresFreestock = options.tiresFreestock.filter((tire) => {
      return (
        !existingRequests?.some((request) => {
          return request.requestTires?.some((existingTire) => {
            if (existingTire.tire.car_position == null) {
              return false;
            }
            return tire.label?.includes(existingTire.tire.car_position);
          });
        }) ||
        request?.requestTires?.some((currentTire) => {
          if (currentTire.tire.label == null) {
            return false;
          }
          return currentTire.tire.label.includes(tire.label);
        })
      );
    });

    return options;
  }, [existingRequests, tireset, request]);

  const entitySelectOptions: { relation_id: EntitySelectFieldModel } =
    useMemo(() => {
      return {
        relation_id: {
          entity: "Relation",
          prefix: "relations",
          schema: relationSchema,
          limit: 100,
          filters: {
            searchBy: "SearchByRootRelation",
            fields: {},
          },
        },
      };
    }, []);

  const datePickerOptions: { pickup_and_delivery_date: datePickerOptions } = {
    pickup_and_delivery_date: {
      filterDate: (date) => {
        // Disable the weekend.
        const day = date.getDay();
        return day !== 0 && day !== 6;
      },
      monthsShown: 2,
    },
  };

  // The administrators can change the date before the delivery date.
  if (!hasRole("super-admin") && !hasRole("dbh-manager")) {
    if (tireset?.getPickupDeliveryDates?.firstDate) {
      datePickerOptions.pickup_and_delivery_date.minDate = new Date(
        tireset.getPickupDeliveryDates.firstDate,
      );
    }
  } else {
    if (tireset?.getPickupDeliveryDates?.firstDate) {
      datePickerOptions.pickup_and_delivery_date.highlightDates = [
        {
          "fw-bold": [new Date(tireset?.getPickupDeliveryDates?.firstDate)],
        },
      ];
    }
  }

  if (tireset?.getPickupDeliveryDates?.forbiddenDates) {
    datePickerOptions.pickup_and_delivery_date.excludeDates =
      tireset?.getPickupDeliveryDates?.forbiddenDates.map((item) => {
        return new Date(item);
      });
  }

  const canEditRequest = useMemo(() => {
    if (hasRole("super-admin")) {
      return true;
    }

    if (request?.state !== undefined && isRequestFinished(request.state)) {
      return false;
    }

    return (
      hasRole("dbh-manager") ||
      (hasRole("dealer-employee") &&
        (request?.availableTransitions?.length ?? 0) > 0) ||
      request === undefined
    );
  }, [hasRole, request, request?.availableTransitions, request?.state]);

  const crudForm = useCrudForm({
    schema: requestSchema,
    prefix: "requests",
    createMutation: "upsertRequest",
    updateMutation: "upsertRequest",
    deleteMutation:
      hasRole("super-admin") || hasRole("dbh-manager")
        ? "deleteRequest"
        : undefined,
    showSubmitButton: hasRole("dbh-manager") || canEditRequest,
    submitButtonLabel:
      submitButtonLabel ??
      (request?.state === "concept" ? t("request.save_and_submit") : ""),
    disabledFields: [
      ...(!canEditRequest ? ["type"] : []),
      ...(hasRole("super-admin") || hasRole("dbh-manager")
        ? ["tire_type", "licence_plate", "updated_at"]
        : [
            "source_type",
            "type",
            "source_id",
            "in_out_storage",
            "updated_at",
            "licence_plate",
            "tire_type",
            ...(request !== undefined && request?.state !== "concept"
              ? [
                  "work_order_number",
                  "invoice_status",
                  "pickup_and_delivery_date",
                  "relation_id",
                ]
              : []),
          ]),
    ],
    hiddenFields: [
      (request?.freestock == undefined || !request?.freestock) &&
      (tireset?.car?.freestock_tire == undefined ||
        !tireset?.car?.freestock_tire)
        ? "tiresFreestock"
        : undefined,
      !request || !(hasRole("super-admin") || hasRole("dbh-manager"))
        ? "invoice_status"
        : undefined,
    ],
    defaultValues: defaultValues,
    queryExclude: [
      "updated_at",
      "licence_plate",
      "license_plate_label",
      "tire_type",
      "source_id",
      "tiresFreestock",
    ],
    mutationExclude: [
      "updated_at",
      "licence_plate",
      "license_plate_label",
      "tire_type",
      "stateFlow",
      "availableTransitions",
      "tiresFreestock",
      "source",
      "freestock",
      "is_from_dbb_api",
      "packingSlip",
    ],
    onAfterSubmit: (result) => {
      dispatch(setLatestRequest(result));
      const res = result as RequestResponseModel;
      if (res?.id) {
        void dispatch(getRequest(String(res.id)));
      }
      if (hasRole("super-admin") || hasRole("dbh-manager")) {
        void dispatch(fetchEvents(new Date(Date.now() - 10000))).then(
          (data) => {
            const payload = data.payload as {
              data: {
                id: number;
                type: string;
                status: string;
                message: string;
              }[];
            };
            payload.data.forEach((event) => {
              if (event.type === "ROUTE_DEFINITIVE") {
                toast.error(event.message);
              }
            });
          },
        );
      }

      navigate(
        "/requests/" + (res?.id ?? (result as RequestResponseModel)?.id),
        {
          replace: true,
        },
      );
    },
    onBeforeSubmit: (result) => {
      const inputResult = result as requestMutationModel & {
        pickup_and_delivery_date?: Date | string;
        in_out_storage?: Date | string;
        tiresFreestock?: number[];
      };
      inputResult.requestTires = {};

      // Format dates to avoid timezone issues
      if (inputResult.pickup_and_delivery_date) {
        const originalDate = inputResult.pickup_and_delivery_date;
        const formattedDate = formatDateForServer(originalDate);
        if (formattedDate) {
          inputResult.pickup_and_delivery_date = formattedDate;
        }
      }

      if (inputResult.in_out_storage) {
        const originalDate = inputResult.in_out_storage;
        const formattedDate = formatDateForServer(originalDate);
        if (formattedDate) {
          inputResult.in_out_storage = formattedDate;
        }
      }

      const requestTires =
        inputResult?.tiresFreestock?.map((tire_id: number) => {
          const existing_tire = request?.requestTires?.find(
            (item) => item.tire.id == tire_id,
          );

          return {
            id: existing_tire?.id ?? undefined,
            tire_id: tire_id,
          };
        }) ?? [];
      if (requestTires.length > 0) {
        inputResult.requestTires.upsert = requestTires;
      }

      const removed_tires = request?.requestTires?.filter(
        (item) =>
          !inputResult?.tiresFreestock?.includes(parseInt(item.tire.id + "")),
      );
      const removed_ids =
        removed_tires?.map((item) => {
          return item.id;
        }) || [];
      if (removed_ids.length > 0) {
        inputResult.requestTires.delete = removed_ids;
      }

      return result;
    },
    formTemplate: RequestTiresetFormTemplate,
    id: request?.id ? parseInt("" + request?.id) : undefined,
    selectOptions: selectOptions,
    datePickerOptions: datePickerOptions,
    entitySelectOptions: entitySelectOptions,
  });

  const updateState = (state: string) => {
    if (state === "submitted") {
      const formPickupDate =
        crudForm.formLogic.state.values.pickup_and_delivery_date;
      const actualPickupDate = request?.pickup_and_delivery_date;

      // Check if the form date is missing or invalid
      if (
        !formPickupDate ||
        !actualPickupDate ||
        !(formPickupDate instanceof Date) ||
        isNaN(formPickupDate.getTime())
      ) {
        void Swal.fire({
          icon: "error",
          title: t("request.pickup_delivery_date_required_title"),
          text: t("request.pickup_delivery_date_required_text"),
        });
        return;
      }

      if (datePickerOptions.pickup_and_delivery_date.minDate) {
        const minDate = new Date(
          datePickerOptions.pickup_and_delivery_date.minDate,
        );
        minDate.setHours(0, 0, 0, 0);
        const selectedDate = new Date(formPickupDate);
        selectedDate.setHours(0, 0, 0, 0);
        const pickupDate = actualPickupDate ? new Date(actualPickupDate) : null;
        if (pickupDate) {
          pickupDate.setHours(0, 0, 0, 0);
        }

        if (pickupDate && pickupDate < minDate) {
          if (formPickupDate && selectedDate >= minDate) {
            void Swal.fire({
              icon: "warning",
              title: t("request.pickup_delivery_date_missing_actual_title"),
              text: t("request.pickup_delivery_date_missing_actual_text", {
                date: new Intl.DateTimeFormat("nl-NL")
                  .format(minDate)
                  .replace(/\//g, "-"),
              }),
            });
          } else {
            void Swal.fire({
              icon: "error",
              title: t("request.pickup_delivery_date_too_early_title"),
              text: t("request.pickup_delivery_date_too_early_text", {
                date: new Intl.DateTimeFormat("nl-NL")
                  .format(minDate)
                  .replace(/\//g, "-"),
              }),
            });
          }
          return;
        }
      }
    }
    if (state == "refused") {
      void Swal.fire({
        title: t("Please state the reason"),
        input: "text",
        confirmButtonText: t("Continue"),
        inputValidator: (value) => {
          if (!value) {
            return t("A reason must be given");
          }
        },
      }).then((result) => {
        if (result.value) {
          if (request?.id) {
            void dispatch(
              updateRequestState({
                id: request.id,
                state,
                comment: result.value,
              }),
            ).then(() => {
              void dispatch(getRequest("" + request.id));
            });
          }
        }
      });
    } else {
      if (request?.id) {
        void dispatch(updateRequestState({ id: request.id, state })).then(
          () => {
            void dispatch(getRequest("" + request.id));
          },
        );
      }
    }
  };

  const title = t("Request");

  const customFilters: CustomFilters = useMemo(() => {
    return {
      where: {
        type: "SearchByRootActivityInput",
        value: {
          anyOf: [
            {
              allOf: [
                {
                  field: {
                    subject_type: {
                      equal: "request",
                    },
                  },
                },
                {
                  field: {
                    subject_id: {
                      equal: request?.id?.toString(),
                    },
                  },
                },
              ],
            },
            {
              allOf: [
                {
                  field: {
                    subject_type: {
                      equal: "tireset",
                    },
                  },
                },
                {
                  field: {
                    subject_id: {
                      equal: tireset?.id?.toString(),
                    },
                  },
                },
              ],
            },
            ...(tireset?.tires?.map((tire) => ({
              allOf: [
                {
                  field: {
                    subject_type: {
                      equal: "tire",
                    },
                  },
                },
                {
                  field: {
                    subject_id: {
                      equal: tire.id?.toString(),
                    },
                  },
                },
              ],
            })) || []),
          ],
        },
      },
      __typename: "custom",
    };
  }, [tireset?.id, request?.id]);

  return (
    <>
      {request && (
        <LayoutContent>
          <Content>
            <ContentHeader title={t("Request status")} className="mb-3">
              {request?.source?.sleep_set && (
                <Badge bg={"primary"} className={"me-2"}>
                  {t("request.sleepset")}
                </Badge>
              )}
              {request?.is_from_dbb_api && (
                <Badge bg={"primary"} className={"me-2"}>
                  {t("request.dbb_api")}
                </Badge>
              )}

              {request.packingSlip ? (
                <Link
                  className={"mt-2"}
                  to={"/packing-slips/edit/" + request?.packingSlip?.id}
                >
                  <Button variant={"success"} size={"sm"}>
                    {t("request.packingslip.planned", {
                      date: (() => {
                        const date = new Date(
                          request?.packingSlip?.date as string,
                        );
                        return new Intl.DateTimeFormat("en-GB")
                          .format(date)
                          .replace(/\//g, "-");
                      })(),
                    })}
                  </Button>
                </Link>
              ) : (
                <Badge bg={"secondary"} className={"me-2"}>
                  {t("request.packingslip.not_planned")}
                </Badge>
              )}
            </ContentHeader>
            <div className={"p-4"}>
              {request.stateFlow && (
                <RequestProgressBar
                  stateFlow={request.stateFlow}
                  size={"large"}
                />
              )}
            </div>

            <div>
              {request?.comment && (
                <div className="mt-4 mb-0 alert alert-light">
                  <strong>{t("Comment")}:</strong>
                  <br />
                  {request.comment}
                </div>
              )}
            </div>
          </Content>
        </LayoutContent>
      )}

      <LayoutContent>
        <Content md={7}>
          <ContentHeader title={t(title)} className="mb-3" />
          {crudForm.form}
        </Content>
        <div className={"col-md-5"}>
          <Content>
            {crudForm.manageHeader}
            {canEditRequest &&
              (hasRole("super-admin") ||
                hasRole("dbh-manager") ||
                hasRole("dealer-employee")) && (
                <div className={"mb-4 mt-2"}>
                  {request?.availableTransitions?.map((transition: string) => {
                    return (
                      <Button
                        key={transition}
                        className={"me-2 btn-sm btn-" + transition}
                        onClick={() => updateState(transition)}
                      >
                        {t(transition + ".transition")}
                      </Button>
                    );
                  })}
                </div>
              )}

            <div
              className={"d-flex justify-content-between align-items-center"}
            >
              {crudForm.manage}
              <div className={"mt-3"}>
                {request?.source?.sticker != undefined && (
                  <Link
                    className={"mt-2"}
                    to={request?.source?.sticker.pdfUrl}
                    target={"_blank"}
                  >
                    <Button variant={"outline-primary"}>
                      {t("sticker.open")}
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </Content>
          {request && <EntityLogs entity="request" filters={customFilters} />}
        </div>
      </LayoutContent>
    </>
  );
};

export default RequestMutateForm;
